import ErrorIcon from '@mui/icons-material/Error';
import { CORE_TYPES } from '@/engine/app/modules/core';

import { observer } from 'mobx-react';
import { useAlgorithmValue } from '@/components/common/useAlgorithmValue';
import { useInjection } from 'inversify-react';
import type { IValue } from '@/engine/DataFlow/Graph';
import type { ValueConstructor } from '@/engine/DataFlow/DataFlow';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';
import type { ViewsService } from '@/engine/app/Views.service.ts';

import { TYPES } from '@/engine/ioc';

export interface Props<T extends IValue> {
  descriptor: string;
  constructor: ValueConstructor<T>;
  valueKey: keyof T;
  formatting?: (n: number) => string;
  isUndefinedOk?: boolean;
}

export const defaultFormatting = (n: number): string => n?.toFixed(1);

export const UpdatableValue = observer(
  <T extends IValue>({ descriptor, constructor, valueKey, formatting, isUndefinedOk }: Props<T>): JSX.Element => {
    const formatFn = formatting || defaultFormatting;

    const value = useAlgorithmValue(descriptor, constructor)?.[valueKey];
    const loggerService = useInjection<ObservabilityLoggerService>(CORE_TYPES.ObservabilityLoggerService);
    const viewsService = useInjection<ViewsService>(TYPES.ViewsService);

    if (value === undefined) {
      if (isUndefinedOk) {
        return <>{'-'}</>;
      } else {
        const vm = viewsService.currentView?.getVmByDescriptor(descriptor);

        if (!vm) return <>{'-'}</>;

        loggerService.dispatchUndefinedValueErrorThrowing(vm);
        return <ErrorIcon color="warning" fontSize="small" />;
      }
    } else {
      return <>{formatFn(value as number)}</>;
    }
  }
);
